import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  UniversalPage,
  UniversalTable,
  UniversalEmptyState,
  Card,
  Button,
  Modal,
  ModalBody,
  StatusBadge,
  type UniversalTableColumn,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
} from '@afrilink/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { salesService, leadPaymentBadge, packageLabel, type LeadRow, type LeadOrder } from '@/domains/sales/services/salesService';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import { AddNoteForm } from '../components/AddNoteForm';
import { LeadRowActions } from '../components/LeadRowActions';

function shortId(id: string): string { return id.slice(0, 8); }

function leadDomain(o: LeadOrder | undefined): string {
  if (!o) return '—';
  if (o.fqdn_mode === 'later') return 'To be decided';
  return o.domain_name ?? '—';
}

/**
 * SalesLeadsPage — manager+ view of all leads in scope (company or all).
 * Bulk-assign deferred to a v1.1 polish — manager today opens each lead and
 * uses the assigned-agent picker on the detail page.
 */

export default function SalesLeadsPage() {
  const { staffRole } = useStaffRole();
  const [rows, setRows] = useState<LeadRow[]>([]);
  const [orders, setOrders] = useState<Map<string, LeadOrder>>(new Map());
  const [agentNames, setAgentNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noteFor, setNoteFor] = useState<LeadRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    salesService
      .listLeads()
      .then(async (data) => {
        if (cancelled) return;
        setRows(data);
        // Resolve agent display_names + each lead's selected package/domain.
        const agentIds = data.map((r) => r.assigned_agent_id).filter((x): x is string => !!x);
        const [names, orderMap] = await Promise.all([
          salesService.resolveAgentNames(agentIds),
          salesService.listLeadOrders(),
        ]);
        if (!cancelled) {
          setAgentNames(names);
          setOrders(orderMap);
          setLoading(false);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Leads are a STOCK set — AR-1.3 names `listLeads()` explicitly as the example
   * that must never receive a date bound, because it selects on CURRENT stage
   * (`lead` / `quoted`). So the date control is labelled `Created`, never
   * "Period": filtering it silently turns "open leads" into "leads created in
   * period", which is a different number that looks equally legitimate.
   */
  const table = useTableFilters<LeadRow>({
    rows,
    dateMode: 'client',
    dateLabel: 'Created',
    dateOf: (r) => r.created_at,
    searchText: (r) =>
      [r.full_name, r.email ?? '', r.phone ?? '', r.company ?? ''].join(' '),
  });

  const columns = useMemo<UniversalTableColumn<LeadRow>[]>(
    () => [
      {
        key: 'full_name',
        header: 'Lead',
        render: (r) => (
          <Link to={`/sales/leads/${r.id}`} className="font-medium text-primary hover:underline">
            {r.full_name}
          </Link>
        ),
      },
      { key: 'company', header: 'Company', render: (r) => r.company ?? '—' },
      // packageLabel, not plan_name: plan_name is hosting-only, so non-hosting
      // orders rendered as a bare dash (D2, 2026-07-29).
      { key: 'package', header: 'Package', render: (r) => packageLabel(orders.get(r.id)) ?? <span className="text-muted-foreground">—</span> },
      { key: 'domain', header: 'Domain', render: (r) => <span className="tabular-nums">{leadDomain(orders.get(r.id))}</span> },
      { key: 'payment', header: 'Payment', render: (r) => <PaymentCell state={orders.get(r.id)?.payment_state} /> },
      { key: 'sales_stage', header: 'Stage', render: (r) => <StageBadge stage={r.sales_stage} /> },
      { key: 'assigned_agent_id', header: 'Assigned', render: (r) => (r.assigned_agent_id ? (agentNames[r.assigned_agent_id] ?? shortId(r.assigned_agent_id)) : <span className="text-muted-foreground">Unassigned</span>) },
      {
        key: 'actions',
        header: '',
        align: 'right',
        render: (r) => (
          <LeadRowActions row={r} order={orders.get(r.id)} onNote={() => setNoteFor(r)} />
        ),
      },
    ],
    [agentNames, orders]
  );

  return (
    <UniversalPage
      title="Leads"
      description={staffRole === 'admin' ? 'Cross-company' : 'Your company'}
      actions={
        <Link to="/sales/leads/new">
          <Button variant="primary" size="sm">+ Add lead</Button>
        </Link>
      }
    >
      {error && <Card className="border-danger/30 bg-danger/5 p-4 text-sm text-danger">{error}</Card>}
      {loading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card>
      ) : rows.length === 0 ? (
        <UniversalEmptyState
          title="No leads yet"
          description="Add a single lead, or import a batch from CSV."
          action={
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Link to="/sales/leads/new">
                <Button variant="primary">Add lead</Button>
              </Link>
              <Link to="/sales/import">
                <Button variant="outline">Import CSV</Button>
              </Link>
            </div>
          }
        />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <PeriodFilter
              value={table.filters.period}
              from={table.filters.from}
              to={table.filters.to}
              onChange={table.filters.setPeriod}
              onStep={table.stepPeriodBy}
              presets={TABLE_PRESETS}
              variant="menu"
              steppable
              label="Created"
            />
            <SearchField
              value={table.q}
              onValueChange={table.setQ}
              pending={table.isSearching}
              placeholder="Search name, email, phone, company"
              className="ml-auto w-full max-w-xs"
            />
          </div>
          <UniversalTable
            columns={columns}
            data={table.rows}
            getRowKey={(r) => r.id}
            caption={table.caption}
          />
        </>
      )}

      {noteFor && (
        <Modal
          open
          onOpenChange={(o) => { if (!o) setNoteFor(null); }}
          title={`Add note — ${noteFor.full_name}`}
          description="Notes are visible to all sales staff who can see this lead, and are append-only."
          size="wide"
        >
          <ModalBody>
            <AddNoteForm customerProfileId={noteFor.id} autoFocus onAdded={() => setNoteFor(null)} />
          </ModalBody>
        </Modal>
      )}
    </UniversalPage>
  );
}

function StageBadge({ stage }: { stage: string }) {
  const tone =
    stage === 'lead' ? 'info'
    : stage === 'quoted' ? 'pending'
    : stage === 'customer' ? 'active'
    : 'draft';
  return <StatusBadge tone={tone}>{stage}</StatusBadge>;
}

function PaymentCell({ state }: { state: string | null | undefined }) {
  const badge = leadPaymentBadge(state);
  if (!badge) return <span className="text-muted-foreground">—</span>;
  return <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>;
}
